import { useEffect, useState } from 'react';

import { SaleState } from '@src/core/buyerApiClient';
import { useAppDispatch } from '@src/core/hooks';
import { fetchOrder, fetchOrderRequiredFormInputs } from '@src/features/orders/store/order';
import { OrderStatus } from '@src/features/orders/types';
import { fetchSale, takeConfirmedSale } from '@src/stores/sale';

import type { ConfirmationSource, ConfirmationTarget } from './types';

export const CONFIRMATION_POLL_INTERVAL_MS = 2000;

const SOURCES: Record<ConfirmationTarget['type'], ConfirmationSource> = {
  order: {
    fetch: async (dispatch, id) => {
      const order = await dispatch(fetchOrder(id));
      return order && { status: order.status, handOff: () => dispatch(fetchOrderRequiredFormInputs(order)) };
    },
    awaitingStatus: OrderStatus.AwaitingConfirmation,
    confirmedStatuses: [OrderStatus.Created],
  },
  sale: {
    fetch: async (dispatch, id) => {
      const sale = await dispatch(fetchSale(id));
      return sale && { status: sale.state, handOff: () => dispatch(takeConfirmedSale(sale)) };
    },
    awaitingStatus: SaleState.AwaitingConfirmation,
    // A sale with nothing left to pay (zero total, external) is confirmed straight into PaymentComplete;
    // handed on, the DirectLink payment step tells the buyer it is already paid.
    confirmedStatuses: [SaleState.Created, SaleState.PaymentComplete],
  },
};

/**
 * Polls the order or sale while the supplier confirms availability. A confirmed status hands it to the
 * regular checkout path (form inputs, then the order or sale itself); any other status ends the wait as
 * unavailable. The store keeps the awaiting one meanwhile, so the checkout stays on this step to show the
 * outcome. Polling only ends with the step: the checkout leaves it once the store takes the confirmed one.
 */
export const useConfirmationPoll = (target: ConfirmationTarget | undefined) => {
  const dispatch = useAppDispatch();
  const [unavailable, setUnavailable] = useState(false);
  const type = target?.type;
  const id = target?.id;

  useEffect(() => {
    if (!type || !id) {
      return;
    }

    const source = SOURCES[type];
    let active = true;
    let timeoutId: ReturnType<typeof setTimeout>;

    const scheduleNextPoll = () => {
      timeoutId = setTimeout(() => {
        void poll();
      }, CONFIRMATION_POLL_INTERVAL_MS);
    };

    const poll = async () => {
      // A failed request says nothing about availability, so it is just retried on the next tick.
      const snapshot = await source.fetch(dispatch, id).catch(() => undefined);

      if (!active) {
        return;
      }

      if (snapshot && source.confirmedStatuses.includes(snapshot.status)) {
        // The store only takes it at the end of the hand-off. One that fails leaves the buyer on this
        // step, so the next tick — still confirmed — hands it over again.
        await snapshot.handOff().catch(() => undefined);

        if (active) {
          scheduleNextPoll();
        }
      } else if (snapshot && snapshot.status !== source.awaitingStatus) {
        setUnavailable(true);
      } else {
        scheduleNextPoll();
      }
    };

    scheduleNextPoll();

    return () => {
      active = false;
      clearTimeout(timeoutId);
    };
  }, [dispatch, type, id]);

  return { unavailable };
};
